import { forwardRef, useRef } from 'react';
import classnames from 'classnames';

import { keyboardMatch, keyboardKeys } from '@hh.ru/magritte-common-keyboard';
import { useDisabled, DisabledContext } from '@hh.ru/magritte-common-use-disabled';
import type { PolymorphicForwardRefRenderFunc, PolymorphicComponentWithRef } from '@hh.ru/magritte-types';
import { IconDynamic, isIconComponent } from '@hh.ru/magritte-ui-icon';
import VerticallyCenteredIcon from '@hh.ru/magritte-ui-link/VerticallyCenteredIcon';
import type { LinkProps } from '@hh.ru/magritte-ui-link/types';
import { Text } from '@hh.ru/magritte-ui-typography';

import styles from './link.less';

const TYPOGRAPHY_WITH_ICON = ['subtitle-1-semibold', 'label-2-regular', 'label-3-regular'];

const TYPOGRAPHY_WITH_BIG_ICON = ['subtitle-1-semibold'];

export const LinkForwardRefRenderFunc: PolymorphicForwardRefRenderFunc<LinkProps, 'a'> = (props, ref) => {
    const {
        Element = 'a',
        children,
        mode = 'primary',
        style: _style = 'accent',
        inline = false,
        disabled = false,
        iconLeft,
        iconRight,
        href,
        typography = 'label-2-regular',
        enableVisited = false,
        underlined = false,
        'data-qa': dataQa = 'link',
        'aria-label': ariaLabel,
        ...otherProps
    } = props;

    const textRef = useRef<HTMLSpanElement>(null);
    const forceDisabled = useDisabled();
    const isComponentDisabled = forceDisabled || disabled;
    const withIcon = TYPOGRAPHY_WITH_ICON.includes(typography);
    const withBigIcon = TYPOGRAPHY_WITH_BIG_ICON.includes(typography);
    // Для mode 'tertiary' доступен только style 'neutral'
    const style = mode === 'tertiary' ? 'neutral' : _style;
    const iconLeftWithColor =
        iconLeft &&
        isIconComponent(iconLeft) &&
        (!!iconLeft.props.initial || !!iconLeft.props.highlighted || !!iconLeft.props.disabled);

    const iconRightWithColor =
        iconRight &&
        isIconComponent(iconRight) &&
        (!!iconRight.props.initial || !!iconRight.props.highlighted || !!iconRight.props.disabled);

    let additionalAttributes: Record<string, unknown>;

    if (Element === 'button') {
        additionalAttributes = {
            type: 'button',
        };
    } else {
        additionalAttributes = {
            tabIndex: isComponentDisabled ? -1 : 0,
            href,
        };
    }

    if (ref) {
        additionalAttributes.ref = ref;
    }

    const handleKeyDown: React.KeyboardEventHandler = (e) => {
        // Поддержка перехода по ссылке при нажатии Enter в случае кастомного элемента
        if (keyboardMatch(e.nativeEvent, keyboardKeys.Enter)) {
            // Кликаю не по кастомному элементу, а внутри него, тк кастомный может не поддерживать ни ref, ни onClick
            textRef.current?.click();
            e.stopPropagation();
            e.preventDefault();
        }
    };

    const renderTextAndRightIcon = () => (
        <>
            <span ref={textRef} data-qa={`${dataQa}-text`} className={styles.text}>
                {children}
            </span>
            {withIcon && iconRight && (
                <span
                    className={classnames(styles.linkIconRight, {
                        [styles.iconWithoutColor]: !iconRightWithColor,
                    })}
                >
                    <VerticallyCenteredIcon icon={iconRight} data-qa={`${dataQa}-icon-external`} />
                </span>
            )}
        </>
    );

    const render = () => (
        <Element
            className={classnames(styles.link, styles[`link_mode_${mode}`], styles[`link_style_${style}`], {
                [styles.link_disabled]: isComponentDisabled,
                [styles.linkBigIcon]: withBigIcon,
                [styles.linkBlock]: !inline,
                [styles.linkEnableVisited]: enableVisited,
                [styles.linkUnderlined]: underlined,
            })}
            data-qa={dataQa}
            disabled={isComponentDisabled}
            aria-label={ariaLabel}
            onKeyDown={handleKeyDown}
            {...additionalAttributes}
            {...otherProps}
        >
            <DisabledContext.Provider value={isComponentDisabled}>
                <IconDynamic>
                    <Text Element="span" typography={typography}>
                        {withIcon && iconLeft ? (
                            <>
                                <span
                                    className={classnames(styles.linkIconLeft, {
                                        [styles.iconWithoutColor]: !iconLeftWithColor,
                                    })}
                                >
                                    <VerticallyCenteredIcon icon={iconLeft} data-qa={`${dataQa}-icon-left`} />
                                </span>
                                <span>{renderTextAndRightIcon()}</span>
                            </>
                        ) : (
                            renderTextAndRightIcon()
                        )}
                    </Text>
                </IconDynamic>
            </DisabledContext.Provider>
        </Element>
    );

    return inline ? render() : <div>{render()}</div>;
};

export const Link: PolymorphicComponentWithRef<LinkProps, 'a'> = forwardRef(LinkForwardRefRenderFunc);
const LinkWithFlag = Link as typeof Link & { isLink: true };
LinkWithFlag.isLink = true;
